import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import useSEO from "@/hooks/useSEO";
import ContactForm from "./components/ContactForm";

const contactItems = [
  {
    icon: "ri-map-pin-2-line",
    label: "品牌地址",
    value: "上海市静安区甜山路 88 号糖心创意园 6 楼",
    copy: "上海市静安区甜山路88号糖心创意园6楼",
  },
  {
    icon: "ri-phone-line",
    label: "联系电话",
    value: "400-820-8899",
    copy: "400-820-8899",
  },
  {
    icon: "ri-mail-line",
    label: "电子邮箱",
    value: "hello@tangxin-vlgo.com",
    copy: "hello@tangxin-vlgo.com",
  },
  {
    icon: "ri-time-line",
    label: "工作时间",
    value: "周一至周五 09:00 - 18:00",
    copy: "",
  },
];

export default function Contact() {
  useSEO({
    title: "联系我们 - 糖心VLGO官方联系方式与商务合作",
    description:
      "联系糖心VLGO官方团队，获取品牌地址、电话、邮箱等联系方式，并可通过在线留言表单进行咨询、渠道合作与媒体采访邀约。",
    keywords: "糖心VLGO联系,糖心VLGO电话,糖心VLGO合作,糖心VLGO留言",
  });

  const [copied, setCopied] = useState("");

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(text);
        setTimeout(() => setCopied(""), 1800);
      })
      .catch(() => {
        setCopied("");
      });
  };

  return (
    <SiteLayout>
      <PageHero
        title="联系我们"
        breadcrumb="联系我们"
        subtitle="无论是产品咨询、渠道合作还是媒体采访，欢迎随时与糖心VLGO 官方团队取得联系。"
        image="https://readdy.ai/api/search-image?query=Warm%20coral%20and%20cream%20abstract%20background%20with%20soft%20communication%20and%20location%20motifs%2C%20elegant%20minimal%20brand%20banner%2C%20soft%20lighting%2C%20high%20detail&width=1920&height=700&seq=tx-contact-hero&orientation=landscape"
      />

      <section className="w-full px-4 md:px-10 py-16 md:py-20 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          {/* 联系信息 */}
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
              <span className="w-6 h-[2px] bg-accent-500"></span>
              联系方式
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
              我们随时在这里
            </h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactItems.map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg bg-background-50 border border-background-200 p-5"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg leading-none`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-500">{item.label}</span>
                  </div>
                  <p className="mt-3 text-sm text-foreground-900 leading-relaxed">{item.value}</p>
                  {item.copy && (
                    <button
                      type="button"
                      onClick={() => handleCopy(item.copy)}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 cursor-pointer hover:text-primary-700"
                    >
                      <i className="ri-file-copy-line leading-none"></i>
                      {copied === item.copy ? "已复制" : "点击复制"}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* 社交账号 */}
            <div className="mt-8 rounded-lg bg-secondary-50 border border-secondary-200 p-6 flex flex-col sm:flex-row items-center gap-6">
              <div className="w-32 h-32 flex items-center justify-center rounded-lg bg-background-50 border border-background-200 shrink-0">
                <div className="flex flex-col items-center">
                  <i className="ri-qr-code-line text-4xl text-foreground-800 leading-none"></i>
                  <span className="mt-1 text-[10px] text-foreground-500">演示二维码</span>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">官方公众号</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  扫码关注糖心VLGO 官方公众号，第一时间获取新品资讯、品牌活动与会员福利。
                </p>
                <p className="mt-2 text-xs text-foreground-500">
                  （此处为演示占位，未接入真实二维码）
                </p>
              </div>
            </div>
          </div>

          {/* 留言表单 */}
          <div>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* 地图 */}
      <section className="w-full bg-secondary-50">
        <div className="w-full px-4 md:px-10 py-16 md:py-20">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
            <span className="w-6 h-[2px] bg-accent-500"></span>
            办公地点
          </span>
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight">
            来糖心创意园找我们
          </h2>
          <div className="mt-8 w-full h-[360px] md:h-[440px] rounded-lg overflow-hidden border border-background-200 bg-background-100">
            <iframe
              title="糖心VLGO 办公地点地图"
              src="https://mvvtirc.cn/maps?q=%E4%B8%8A%E6%B5%B7%E5%B8%82%E9%9D%99%E5%AE%89%E5%8C%BA&output=embed"
              className="w-full h-full"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}